'use client'

import { usePageData } from '@/contexts/PageDataContext'
import type { Maybe, VideoData } from '@/frontend-types'
import { tagCMSBlock } from '@/helpers/layout'
import { cx } from 'cva'
import { motion } from 'motion/react'
import type { FC } from 'react'
import { useId, useState } from 'react'
import { Button } from '../atoms/Button'
import { Corridor } from '../atoms/Corridor'
import { Grid } from '../atoms/Grid'
import { PlaceholderImage } from '../atoms/PlaceholderImage'
import { Video } from '../atoms/Video'
import HomeHeroAssets from '../molecules/HomeHeroAssets'
import { VideoOverlay } from '../molecules/VideoOverlay'

export type HomeHeroType = {
	title: Maybe<string>
	backgroundVideo?: VideoData
	overlayVideo?: VideoData
}

const HomeHero: FC<HomeHeroType> = ({ title, backgroundVideo, overlayVideo }) => {
	const pageData = usePageData()
	const titleOrPageTitle = title || pageData.title
	const headingWords = titleOrPageTitle?.split(' ') || []

	const videoOverlayId = useId()
	const [isVideoOverlayOpen, setIsVideoOverlayOpen] = useState(false)

	return (
		<section className={cx(tagCMSBlock(['hero']), 'flex flex-col h-[60svh] min-h-100 mb-5xl')}>
			<div className="relative z-10 flex-1">
				<Corridor className="flex h-full flex-col justify-end pb-5xl">
					<Grid>
						<div className="relative z-10 col-span-3 flex flex-col items-start justify-center gap-y-4xl tablet-p:col-span-5 tablet-l:col-span-7">
							<h1 className="text-h1 text-white text-balance">
								{headingWords.map((word, index) => (
									<motion.span
										key={`${word}-${index}`}
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{
											duration: 0.2,
											delay: index * 0.05,
											ease: 'easeInOut',
										}}
										className="inline-block"
									>
										{word}
										{index < headingWords.length - 1 && '\u00A0'}
									</motion.span>
								))}
							</h1>
							{overlayVideo && (
								<motion.div
									initial={{ opacity: 0, y: 20, pointerEvents: 'none' }}
									animate={{ opacity: 1, y: 0, pointerEvents: 'auto' }}
									transition={{
										duration: 0.2,
										delay: (headingWords.length - 1) * 0.05 + 0.2,
										ease: 'easeInOut',
									}}
								>
									<Button
										id="watch-video"
										label="Watch video"
										icon={['fal', 'play']}
										theme="blue"
										onClick={() => setIsVideoOverlayOpen(true)}
										aria-controls={videoOverlayId}
										aria-expanded={isVideoOverlayOpen}
										aria-haspopup="dialog"
									/>
								</motion.div>
							)}
						</div>
					</Grid>
				</Corridor>

				<div className="absolute bottom-0 left-0 z-0 h-full w-full" aria-hidden="true">
					{/* Background image and clipPath */}
					<div className="h-full w-full">
						{backgroundVideo ? (
							<Video
								video={backgroundVideo}
								muted={true}
								playsInline={true}
								loop={true}
								autoPlay={true}
								className="object-cover pointer-events-none"
							/>
						) : (
							<PlaceholderImage />
						)}
					</div>
					<div
						className="absolute inset-0 bg-black opacity-20 tablet-l:opacity-30 z-5 pointer-events-none"
						aria-hidden="true"
					/>
				</div>

				{/* SVG assets */}
				<HomeHeroAssets />
			</div>
			{/* Video Overlay */}
			{overlayVideo && (
				<VideoOverlay
					id={videoOverlayId}
					video={overlayVideo}
					open={isVideoOverlayOpen}
					onClose={() => setIsVideoOverlayOpen(false)}
				/>
			)}
		</section>
	)
}

export default HomeHero
